import { arrayToString, leftPad } from './string'
import config from '../../config'
import { enGB as en, da, sv, nb, nl } from 'date-fns/locale'
import format from 'date-fns/format'
import formatDistanceStrict from 'date-fns/formatDistanceStrict'

import i18next from 'i18next'

const locales = { en, da, sv, nb, nl }

/**
 * Relative time part names used in a RelativeTimeMap
 * @enum {{string}}
 */
export const RelativeTimeMapPart = {
  SECONDS: 'seconds',
  MINUTES: 'minutes',
  HOURS: 'hours',
  DAYS: 'days',
  WEEKS: 'weeks',
}

/**
 * Represents time as an object divided into weeks, days, hours, minutes, numbers and seconds.
 * All values are optional, but should always be integers and never exceed the partition above itself.
 * Years and months aren't included because of their fussiness.
 *
 * @typedef {Object<RelativeTimeMapPart, Number>} RelativeTimeMap
 */

/**
 * The amount of milliseconds in a second.
 * @type {number}
 */
export const SECOND = 1000

/**
 * The amount of milliseconds in a minute.
 * @type {number}
 */
export const MINUTE = SECOND * 60

/**
 * The amount of milliseconds in an hour.
 * @type {number}
 */
export const HOUR = MINUTE * 60

/**
 * The amount of milliseconds in a day.
 * @type {number}
 */
export const DAY = HOUR * 24

/**
 * The amount of milliseconds in a week.
 * @type {number}
 */
export const WEEK = DAY * 7

/**
 * Converts an amount of milliseconds to a map of relative time parts for easier formatting.
 *
 * @param {number} milliseconds
 * @returns {RelativeTimeMap}
 */
export function msToRelativeTimeMap(milliseconds) {
  return {
    [RelativeTimeMapPart.SECONDS]: Math.floor((milliseconds % MINUTE) / SECOND),
    [RelativeTimeMapPart.MINUTES]: Math.floor((milliseconds % HOUR) / MINUTE),
    [RelativeTimeMapPart.HOURS]: Math.floor((milliseconds % DAY) / HOUR),
    [RelativeTimeMapPart.DAYS]: Math.floor((milliseconds % WEEK) / DAY),
    [RelativeTimeMapPart.WEEKS]: Math.floor(milliseconds / WEEK),
  }
}

/**
 * Formats a relative time map as a human readable string.
 *
 * @param {RelativeTimeMap} relativeTimeMap
 * @param {boolean=} compactTime Set true to display the time as `23:45:34` instead of `23 hours, 45 minutes and 34 seconds`.
 * @param hideSeconds Set true to hide the seconds in the output string.
 * @returns {string}
 */
export function formatRelativeTimeMap(
  relativeTimeMap,
  compactTime,
  hideSeconds
) {
  let returnValue = []

  const weeks = relativeTimeMap[RelativeTimeMapPart.WEEKS] || 0
  const days = relativeTimeMap[RelativeTimeMapPart.DAYS] || 0
  const hours = relativeTimeMap[RelativeTimeMapPart.HOURS] || 0
  const minutes = relativeTimeMap[RelativeTimeMapPart.MINUTES] || 0
  const seconds = relativeTimeMap[RelativeTimeMapPart.SECONDS] || 0

  if (weeks) {
    returnValue.push(i18next.t('date-time.week', { count: weeks, weeks }))
  }

  if (days) {
    returnValue.push(i18next.t('date-time.day', { count: days, days }))
  }

  if (compactTime) {
    if (hideSeconds) {
      // Display hours:minutes
      returnValue.push(`${hours}:${leftPad(minutes, 2)}`)
    } else if (hours) {
      // Display hours:minutes:seconds
      returnValue.push(`${hours}:${leftPad(minutes, 2)}:${leftPad(seconds, 2)}`)
    } else {
      // Display minutes:seconds
      returnValue.push(`${minutes}:${leftPad(seconds, 2)}`)
    }
  } else {
    if (hours) {
      returnValue.push(i18next.t('date-time.hour', { count: hours, hours }))
    }

    if (minutes) {
      returnValue.push(
        i18next.t('date-time.minute', { count: minutes, minutes })
      )
    }

    if (seconds && !hideSeconds) {
      returnValue.push(
        i18next.t('date-time.second', { count: seconds, seconds })
      )
    }
  }

  return arrayToString(returnValue)
}

/**
 * Shows time in the format of "00:00"
 * @param {Date} date
 * @returns {string}
 */
export function getPrettyTime(date) {
  if (!date || !(date instanceof Date)) {
    throw 'date needs to be a Date object'
  }

  // TODO this will need localization. research moment.js or perhaps intl.DateTimeFormat (but the latter formats time wrong).
  // DateTimeFormat outputs danish time as "14.34" which is REALLY weird!! beause we use : usually. >:(

  return formatRelativeTimeMap(
    {
      [RelativeTimeMapPart.HOURS]: date.getHours(),
      [RelativeTimeMapPart.MINUTES]: date.getMinutes(),
    },
    true,
    true
  )
}

/**
 * Creates a pretty, readable date format string, using locale
 * @param {Date} date
 * @returns {string}
 */
export function getPrettyDate(date) {
  if (!date || !(date instanceof Date)) {
    throw 'date needs to be a Date object'
  }

  return format(date, 'do MMM y', {
    locale: locales[config.locale],
  })
}

/**
 * Turns our date string into parts and then a Date object, as this shows consistent dates across all browsers.
 * @param {string} string
 * @returns {Date}
 */
export function stringToDate(string) {
  if (!string || typeof string !== 'string') {
    return null
  }

  const matches = /^((?:19|20)[0-9]{2})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])(?: ([01][0-9]|2[0-4]):([0-5][0-9])(?::([0-5][0-9]))?)?$/.exec(
    string
  )

  if (!matches) {
    return null
  }

  return new Date(
    parseInt(matches[1]), // Year
    parseInt(matches[2]) - 1, // Month
    parseInt(matches[3]), // Date
    parseInt(matches[4]), // Hour
    parseInt(matches[5]), // Minute
    parseInt(matches[6]) // Second
  )
}

/**
 * Take a JS Date object and returns a string formatted as a MySQL DateTime string
 * Can omit seconds if noSecs is true
 * @param {Date} dateObj JS Date object to convert.
 * @param {boolean=} noSecs Optional no seconds.
 * @param {boolean=} noHMS Optional HMS.
 * @return {string} mysql datetime string.
 */
export function dateToString(dateObj, noSecs, noHMS) {
  if (!dateObj || !(dateObj instanceof Date)) {
    return ''
  }

  noSecs = noSecs || false

  let string =
    dateObj.getFullYear() +
    '-' +
    leftPad(dateObj.getMonth() + 1, 2) +
    '-' +
    leftPad(dateObj.getDate(), 2)

  // Only use hour:mins:secs if object is instance of DateTime - else these don't exist
  if (dateObj instanceof Date && !noHMS) {
    string +=
      ' ' +
      leftPad(dateObj.getHours(), 2) +
      ':' +
      leftPad(dateObj.getMinutes(), 2) +
      (noSecs ? '' : ':' + leftPad(dateObj.getSeconds(), 2))
  }

  return string
}

/**
 *
 * @param {Date} date
 * @returns {string}
 */
export function getRelativeTime(date) {
  if (!date || !(date instanceof Date)) {
    throw 'date needs to be a Date object'
  }

  return formatDistanceStrict(date, new Date(), {
    locale: locales[config.locale],
  })
}

/**
 * Formats the given number of milliseconds as seconds, minutes, hours, days and weeks.
 *
 * @param {number} milliseconds
 * @param {boolean=} compactTime
 * @returns {string}
 */
export function formatDuration(milliseconds, compactTime) {
  return formatRelativeTimeMap(msToRelativeTimeMap(milliseconds), compactTime)
}

/// New stuff starts here

/**
 * Common date ranges
 * @enum {string}
 */
export const DateRange = {
  CURRENT_WEEK: 'currentWeek',
  LAST_WEEK: 'lastWeek',
  CURRENT_MONTH: 'currentMonth',
  LAST_MONTH: 'lastMonth',
  CURRENT_YEAR: 'currentYear',
  LAST_YEAR: 'lastYear',
}

/**
 * Common date range units
 * @enum {string}
 */
export const DateRangeUnit = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
}

/**
 * Clones a date
 *
 * @param {Date} date
 * @return {Date}
 */
export function cloneDate(date) {
  return new Date(date.toISOString())
}

/**
 * Clones a date range
 *
 * @param {Array<Date>} dateRange
 * @return {Array<Date>}
 */
export function cloneDateRange(dateRange) {
  return dateRange.map(cloneDate)
}

/**
 * Returns true if the two dates have the exact same timestamp.
 *
 * @param {Date} date1
 * @param {Date} date2
 * @return {boolean}
 */
export function isSameDate(date1, date2) {
  return date1.getTime() === date2.getTime()
}

/**
 * Returns true if the two date ranges have the exact same amount of dates and the dates have the same timestamps.
 *
 * @param {Array<Date>} range1
 * @param {Array<Date>} range2
 * @return {boolean}
 */
export function isSameDateRange(range1, range2) {
  return (
    range1.length === range2.length &&
    !range1.some((date, index) => !isSameDate(date, range2[index]))
  )
}

/**
 * Returns the duration of a given date range or between two given dates.
 *
 * @param {Array<Date>|Date} dateOrRange
 * @param {Date=} date
 * @return {number}
 */
export function getDateRangeDuration(dateOrRange, date) {
  if (arguments.length === 1) {
    if (dateOrRange.length > 1) {
      return Math.abs(dateOrRange[0].getTime() - dateOrRange[1].getTime())
    } else {
      return 0
    }
  } else {
    return Math.abs(dateOrRange.getTime() - date.getTime())
  }
}

/**
 * Creates a pretty, readable date format string, using locale
 * @param {Array<Date>} range
 * @returns {string}
 */
export function getPrettyDateRange(range) {
  return range.map(getPrettyDate).join(' - ')
}

/**
 * Returns the current year.
 * @return {number}
 */
export function getCurrentYear() {
  return new Date().getFullYear()
}

/**
 * Returns a date object representing the first day of the given month (at 00:00:00.000 o'clock)
 *
 * @param {number} month
 * @param {number=} year
 * @return {Date}
 */
export function getFirstDayOfMonth(month, year) {
  return new Date(year || getCurrentYear(), month, 1, 0, 0, 0, 0)
}

/**
 * Returns a date object representing the last day of the given month (at 23:59:59.999 o'clock)
 *
 * @param {number} month
 * @param {number=} year
 * @return {Date}
 */
export function getLastDayOfMonth(month, year) {
  const date = getFirstDayOfMonth(month + 1, year)

  return new Date(date.getTime() - 1)
}

/**
 * Returns a date object representing the first day of the given year (at 00:00:00.000 o'clock)
 *
 * @param {number=} year
 * @return {Date}
 */
export function getFirstDayOfYear(year) {
  return getFirstDayOfMonth(0, year)
}

/**
 * Returns a date object representing the last day of the given year (at 23:59:59.999 o'clock)
 *
 * @param {number=} year
 * @return {Date}
 */
export function getLastDayOfYear(year) {
  return getLastDayOfMonth(11, year)
}

/**
 * Returns a new date with the first time of the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getBeginningOfDay(date) {
  return setBeginningOfDay(cloneDate(date))
}

/**
 * Returns a new date with the first time of the week containing the given date.
 *
 * @param {Date} date
 * @param {number=} weekStartDay
 * @returns {Date}
 */
export function getBeginningOfWeek(date, weekStartDay = 1) {
  const newDate = getBeginningOfDay(date)

  newDate.setDate(newDate.getDate() - newDate.getDay() + weekStartDay)

  if (newDate.getTime() > date.getTime()) {
    newDate.setDate(newDate.getDate() - 7)
  }

  return newDate
}

/**
 * Returns a new date with the first time of the month containing the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getBeginningOfMonth(date) {
  const newDate = getBeginningOfDay(date)

  newDate.setDate(1)

  return newDate
}

/**
 * Returns a new date with the first time of the year containing the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getBeginningOfYear(date) {
  const newDate = new Date(date.getFullYear(), 0, 1)

  return setBeginningOfDay(newDate)
}

/**
 * Returns a new date with the last time of the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getEndOfDay(date) {
  return setEndOfDay(cloneDate(date))
}

/**
 * Returns a new date with the last time of the week containing the given date.
 *
 * @param {Date} date
 * @param {number=} weekStartDay
 * @returns {Date}
 */
export function getEndOfWeek(date, weekStartDay = 1) {
  const newDate = getBeginningOfWeek(date, weekStartDay)

  newDate.setDate(newDate.getDate() + 6)

  return setEndOfDay(newDate)
}

/**
 * Returns a new date with the last time of the month containing the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getEndOfMonth(date) {
  return getLastDayOfMonth(date.getMonth(), date.getFullYear())
}

/**
 * Returns a new date with the first time of the year containing the given date.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getEndOfYear(date) {
  return getLastDayOfMonth(11, date.getFullYear())
}

/**
 * Sets the given date time to 00:00:00.000 o'clock
 *
 * @param {Date} date
 * @returns {Date} The modified date object
 */
export function setBeginningOfDay(date = new Date()) {
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Sets the given date time to 23:59:59.999 o'clock
 *
 * @param {Date} date
 * @returns {Date} The modified date object
 */
export function setEndOfDay(date = new Date()) {
  date.setHours(23, 59, 59, 999)
  return date
}

/**
 * Returns a new Date object set to the start of a given unit period.
 *
 * @param {DateRangeUnit} unit
 * @param {Date} date
 * @param {number} weekStartDay
 * @returns {Date}
 */
function getBeginningOfRange(unit, date = new Date(), weekStartDay = 1) {
  if (!unit || !Object.values(DateRangeUnit).includes(unit)) {
    throw 'Unknown date range unit identifier used'
  }

  switch (unit) {
    case DateRangeUnit.DAY:
      return getBeginningOfDay(date)
    case DateRangeUnit.WEEK:
      return getBeginningOfWeek(date, weekStartDay)
    case DateRangeUnit.MONTH:
      return getBeginningOfMonth(date)
    case DateRangeUnit.YEAR:
      return getBeginningOfYear(date)
  }

  return date
}

/**
 * Returns a new Date object set to the end of a given unit period.
 *
 * @param {DateRangeUnit} unit
 * @param {Date} date
 * @param {number} weekStartDay
 * @returns {Date}
 */
export function getEndOfRange(unit, date = new Date(), weekStartDay = 1) {
  if (!unit || !Object.values(DateRangeUnit).includes(unit)) {
    throw 'Unknown date range unit identifier used'
  }

  switch (unit) {
    case DateRangeUnit.DAY:
      return getEndOfDay(date)
    case DateRangeUnit.WEEK:
      return getEndOfWeek(date, weekStartDay)
    case DateRangeUnit.MONTH:
      return getEndOfMonth(date)
    case DateRangeUnit.YEAR:
      return getEndOfYear(date)
  }

  return date
}

/**
 * A magic method that travels in time periods.
 * The function travels a specified amount of units (ENUM from DateRangeUnit) back and forwards in time.
 * To travel forward in time use a positive amount eg. 1.
 * To travel backwards in time use a negative amount eg. -1.
 *
 * @param {DateRangeUnit} unit
 * @param {Date} date
 * @param {number} amount
 * @returns {Date}
 */
export function timeTravel(unit, date = new Date(), amount = 1) {
  if (!unit || !Object.values(DateRangeUnit).includes(unit)) {
    throw 'Unknown date range unit identifier used'
  }

  switch (unit) {
    case DateRangeUnit.DAY:
      return new Date(date.getTime() + amount * DAY)
    case DateRangeUnit.WEEK:
      return new Date(date.getTime() + amount * WEEK)
    case DateRangeUnit.MONTH:
      return date.getDate() === 1
        ? getFirstDayOfMonth(date.getMonth() + amount, date.getFullYear())
        : getLastDayOfMonth(date.getMonth() + amount, date.getFullYear())
    case DateRangeUnit.YEAR:
      return date.getDate() === 1
        ? getFirstDayOfYear(date.getFullYear() + amount)
        : getLastDayOfYear(date.getFullYear() + amount)
  }

  return date
}

/**
 * Gets a date range based on an identifier
 * @param {DateRange} dateRange
 * @returns {Array<Date>}
 */
export function getDateRangeFromIdentifier(dateRange) {
  if (!dateRange && !Object.values(DateRange).includes(dateRange)) {
    throw 'Unknown date range identifier used'
  }

  let startDate = null
  let endDate = null

  switch (dateRange) {
    case DateRange.CURRENT_WEEK:
      startDate = getBeginningOfRange(DateRangeUnit.WEEK)
      endDate = getEndOfRange(DateRangeUnit.WEEK)
      break
    case DateRange.LAST_WEEK:
      startDate = timeTravel(
        DateRangeUnit.WEEK,
        getBeginningOfRange(DateRangeUnit.WEEK)
      )
      endDate = timeTravel(
        DateRangeUnit.WEEK,
        getEndOfRange(DateRangeUnit.WEEK)
      )
      break
    case DateRange.CURRENT_MONTH:
      startDate = getBeginningOfRange(DateRangeUnit.MONTH)
      endDate = getEndOfRange(DateRangeUnit.MONTH)
      break
    case DateRange.LAST_MONTH:
      startDate = timeTravel(
        DateRangeUnit.MONTH,
        getBeginningOfRange(DateRangeUnit.MONTH)
      )
      endDate = getEndOfRange(
        DateRangeUnit.MONTH,
        timeTravel(DateRangeUnit.MONTH, new Date())
      )
      break
    case DateRange.CURRENT_YEAR:
      startDate = getBeginningOfRange(DateRangeUnit.YEAR)
      endDate = getEndOfRange(DateRangeUnit.YEAR)
      break
    case DateRange.LAST_YEAR:
      startDate = timeTravel(
        DateRangeUnit.YEAR,
        getBeginningOfRange(DateRangeUnit.YEAR)
      )
      endDate = timeTravel(
        DateRangeUnit.YEAR,
        getEndOfRange(DateRangeUnit.YEAR)
      )
      break
  }

  return [cloneDate(startDate), cloneDate(endDate)]
}
